import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  RefreshControl,
  FlatList,
  Platform,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { getStoreArticles, updateStoreArticlePositions, deleteStoreArticlePosition } from '../services/storesService';

const StoreSortScreen = ({ route, navigation }) => {
  const { storeId, storeName } = route.params;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Drag&Drop State (wie in ShoppingScreen)
  const [draggedArticle, setDraggedArticle] = useState(null);
  const [draggedOverArticle, setDraggedOverArticle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDragEffects, setShowDragEffects] = useState(false);
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useFocusEffect(
    useCallback(() => {
      loadArticles();
    }, [storeId])
  );

  // Gefilterte Artikel basierend auf Suchanfrage
  const filteredArticles = useMemo(() => {
    if (!searchQuery.trim()) {
      return articles;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return articles.filter(article => {
      // Suche in Artikelname
      if (article.article_name && article.article_name.toLowerCase().includes(query)) {
        return true;
      }
      
      // Suche in Tags
      if (article.default_tags && article.default_tags.some(tag => 
        tag.toLowerCase().includes(query)
      )) {
        return true;
      }
      
      return false;
    });
  }, [articles, searchQuery]);

  const loadArticles = async () => {
    setLoading(true);
    const result = await getStoreArticles(storeId);
    
    if (result.success) {
      // Sortiere NUR nach position (manuelle Reihenfolge)
      const sortedArticles = result.data.sort((a, b) => {
        return a.position - b.position;
      });
      setArticles(sortedArticles);
    } else {
      Alert.alert('Fehler', result.error);
    }
    
    setLoading(false);
    setRefreshing(false);
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadArticles();
  };

  // Escape-Handler für Drag-Modus
  const handleEscapeDrag = () => {
    if (isDragging) {
      setIsDragging(false);
      setDraggedArticle(null);
      setDraggedOverArticle(null);
      setShowDragEffects(false);
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
    }
  };

  const handleDeleteArticle = (article) => {
    const message = `"${article.article_name}" aus der Sortierliste entfernen?`;
    
    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        deleteArticleAsync(article);
      }
    } else {
      Alert.alert(
        'Artikel entfernen',
        message,
        [
          { text: 'Abbrechen', style: 'cancel' },
          {
            text: 'Entfernen',
            style: 'destructive',
            onPress: () => deleteArticleAsync(article),
          },
        ]
      );
    }
  };

  const deleteArticleAsync = async (article) => {
    const result = await deleteStoreArticlePosition(storeId, article.id);
    if (result.success) {
      // Artikel aus lokaler Liste entfernen
      setArticles(prev => prev.filter(a => a.id !== article.id));
    } else {
      if (Platform.OS === 'web') {
        window.alert('Fehler: ' + result.error);
      } else {
        Alert.alert('Fehler', result.error);
      }
    }
  };

  // Drag&Drop Handler (wie in ShoppingScreen)
  const handleSwipeStart = (e, article) => {
    const touch = e.nativeEvent.touches?.[0] || e.nativeEvent;
    setSwipeStartX(touch.clientX || touch.pageX);
    setSwipeStartY(touch.clientY || touch.pageY);
    setIsSwipeActive(true);
  };

  const handleSwipeMove = (e, article) => {
    if (!isSwipeActive || !swipeStartX || !swipeStartY) return;
    
    const touch = e.nativeEvent.touches?.[0] || e.nativeEvent;
    const deltaX = (touch.clientX || touch.pageX) - swipeStartX;
    const deltaY = (touch.clientY || touch.pageY) - swipeStartY;
    
    // Lange Swipe-Geste: mindestens 100px horizontal, max 50px vertikal (wie in ShoppingScreen)
    // WICHTIG: Nur aktivieren wenn deutlich horizontal (kein Scroll)
    if (Math.abs(deltaX) > 100 && Math.abs(deltaY) < 50 && !isDragging) {
      handleDragStart(article);
      setIsSwipeActive(false);
    }
    
    // Wenn vertikale Bewegung größer ist, ist es ein Scroll - beende Swipe-Erkennung
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 20) {
      setIsSwipeActive(false);
      setSwipeStartX(null);
      setSwipeStartY(null);
    }
  };

  const handleSwipeEnd = () => {
    setIsSwipeActive(false);
    setSwipeStartX(null);
    setSwipeStartY(null);
  };

  const handleDragStart = (article) => {
    // Clear any existing timeout first
    if (dragTimeout) {
      clearTimeout(dragTimeout);
    }
    
    // Erst isDragging setzen, dann draggedArticle
    setIsDragging(true);
    setDraggedArticle(article);
    setShowDragEffects(true);
  };

  const handleDrop = async (targetArticle, position) => {
    if (!isDragging || !draggedArticle) return;
    
    // Erstelle ein vollständiges Target-Item mit Position
    const fullTargetItem = { 
      ...targetArticle, 
      dropPosition: position
    };
    
    // Führe den Drop aus
    await handleDragEnd(fullTargetItem);
  };

  const handleDragEnd = async (targetArticle = null) => {
    const dropTarget = targetArticle || draggedOverArticle;
    
    if (!draggedArticle || !dropTarget) {
      setDraggedArticle(null);
      setDraggedOverArticle(null);
      setIsDragging(false);
      setShowDragEffects(false);
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
      return;
    }
    
    if (draggedArticle.id === dropTarget.id) {
      setDraggedArticle(null);
      setDraggedOverArticle(null);
      setIsDragging(false);
      setShowDragEffects(false);
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
      return;
    }

    try {
      const draggedIndex = articles.findIndex(a => a.id === draggedArticle.id);
      const targetIndex = articles.findIndex(a => a.id === dropTarget.id);

      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Items neu anordnen
      const newArticles = [...articles];
      
      let insertIndex;
      if (dropTarget.dropPosition === 'above') {
        insertIndex = targetIndex;
      } else if (dropTarget.dropPosition === 'below') {
        insertIndex = targetIndex + 1;
      } else {
        insertIndex = targetIndex + 1;
      }
      
      // Entferne das Item und passe insertIndex an
      const [movedArticle] = newArticles.splice(draggedIndex, 1);
      
      if (draggedIndex < targetIndex) {
        insertIndex = insertIndex - 1;
      }
      
      insertIndex = Math.max(0, Math.min(insertIndex, newArticles.length));
      newArticles.splice(insertIndex, 0, movedArticle);

      // Positionen aktualisieren
      const reorderedArticles = newArticles.map((article, index) => ({
        article_id: article.id,
        position: index + 1,
      }));

      // Lokale State aktualisieren
      setArticles(newArticles);
      setSaving(true);

      // Backend aktualisieren
      const result = await updateStoreArticlePositions(storeId, reorderedArticles);
      setSaving(false);
      
      if (result.success) {
        // Erfolg - State bleibt wie ist
        setDraggedArticle(null);
        setDraggedOverArticle(null);
        setIsDragging(false);
        setShowDragEffects(false);
      } else {
        Alert.alert('Fehler', result.error);
        // Bei Fehler: Artikel neu laden
        loadArticles();
      }
    } catch (error) {
      console.error('Fehler beim Sortieren:', error);
      Alert.alert('Fehler', 'Fehler beim Sortieren der Artikel');
      loadArticles();
    } finally {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
    }
  };

  const renderArticle = ({ item, index }) => {
    const isDragged = draggedArticle?.id === item.id;
    const isDraggedOver = draggedOverArticle?.id === item.id;

    return (
      <View key={`article-wrapper-${item.id}`}>
        <Pressable
          style={[
            styles.articleCard,
            isDragged && showDragEffects && styles.articleCardDragging,
            isDraggedOver && showDragEffects && styles.articleCardDraggedOver
          ]}
          onTouchStart={(e) => handleSwipeStart(e, item)}
          onTouchMove={(e) => handleSwipeMove(e, item)}
          onTouchEnd={handleSwipeEnd}
        >
          <View style={styles.articleContent}>
            <View style={styles.articleInfo}>
              <View style={styles.articleNameRow}>
                <Text style={styles.articleName} numberOfLines={1}>
                  {item.article_name}
                </Text>
                {/* Tags in derselben Zeile rechts neben dem Artikelnamen */}
                {item.default_tags && item.default_tags.length > 0 && (
                  <View style={styles.tagsContainer}>
                    {item.default_tags.slice(0, 2).map((tag, index) => (
                      <View key={index} style={styles.tag}>
                        <Text style={styles.tagText}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
            
            {/* Lösch-Button - nur anzeigen wenn nicht im Drag-Modus */}
            {!isDragging && (
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => {
                  console.log('Delete button pressed for:', item.article_name);
                  handleDeleteArticle(item);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color="#d32f2f" />
              </TouchableOpacity>
            )}
            
            {/* X-Button zum Beenden des Drag-Modus - nur für den gedraggten Artikel */}
            {isDragging && isDragged && (
              <Pressable
                style={styles.dragCancelButton}
                onPress={handleEscapeDrag}
              >
                <Ionicons name="close" size={20} color="#fff" />
              </Pressable>
            )}
            
            {/* Drop-Buttons für alle anderen Artikel im Drag-Modus */}
            {isDragging && !isDragged && (
              <View style={styles.dropButtonsContainer}>
                <Pressable
                  style={styles.dropButton}
                  onPress={() => handleDrop(item, 'above')}
                >
                  <Ionicons name="arrow-up" size={16} color="#fff" />
                </Pressable>
                <Pressable
                  style={styles.dropButton}
                  onPress={() => handleDrop(item, 'below')}
                >
                  <Ionicons name="arrow-down" size={16} color="#fff" />
                </Pressable>
              </View>
            )}
          </View>
        </Pressable>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header Info */}
      <View style={styles.headerInfo}>
        <Text style={styles.infoText}>
          Wische einen Artikel horizontal, um den Verschiebemodus zu aktivieren.
        </Text>
        {saving && (
          <View style={styles.savingIndicator}>
            <ActivityIndicator size="small" color="#2E7D32" />
            <Text style={styles.savingText}>Speichere...</Text>
          </View>
        )}
      </View>

      {/* Suchfeld */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Artikel suchen..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {filteredArticles.length === 0 ? (
        <View style={styles.centerContainer}>
          <Ionicons name="list-outline" size={64} color="#ccc" />
          <Text style={styles.emptyText}>Keine Artikel</Text>
          <Text style={styles.emptySubtext}>
            Artikel, die in diesem Laden gekauft wurden, erscheinen hier zum Sortieren.
          </Text>
        </View>
      ) : (
        <FlatList
          data={filteredArticles}
          renderItem={renderArticle}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          scrollEnabled={true}
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh}
              colors={['#2E7D32']}
            />
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  headerInfo: {
    backgroundColor: '#E8F5E9',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#C8E6C9',
  },
  infoText: {
    fontSize: 14,
    color: '#2E7D32',
    textAlign: 'center',
  },
  savingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 8,
  },
  savingText: {
    fontSize: 12,
    color: '#2E7D32',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
    outline: 'none',
    borderWidth: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
  listContent: {
    padding: 8,
  },
  articleCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  articleCardDragging: {
    backgroundColor: '#F1F8E9',
    borderColor: '#2E7D32',
    borderWidth: 2,
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    opacity: 0.7,
  },
  articleCardDraggedOver: {
    borderColor: '#4CAF50',
    borderWidth: 2,
  },
  articleContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  articleInfo: {
    flex: 1,
  },
  articleNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  articleName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
    flexShrink: 1,
    marginRight: 6,
  },
  tagsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginRight: 4,
  },
  tagText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '600',
  },
  dragCancelButton: {
    backgroundColor: '#d32f2f',
    borderRadius: 14,
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  dropButtonsContainer: {
    flexDirection: 'row',
    gap: 4,
    marginLeft: 8,
  },
  dropButton: {
    backgroundColor: '#2E7D32',
    borderRadius: 14,
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteButton: {
    padding: 4,
    marginLeft: 8,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
    textAlign: 'center',
  },
});

export default StoreSortScreen;
